import { useState } from 'react';
import { GripVertical, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useDrag, useDrop } from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';

export type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
  width?: string;
  type?: 'text' | 'number' | 'select' | 'custom';
};

export interface BaseSpreadsheetProps<T> {
  data: T[];
  columns: ColumnConfig[];
  canEdit: boolean;
  onDataChange: (index: number, field: string, value: any) => void;
  onRemove: (id: string) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onAdd: () => void;
  onSave: () => void;
  renderCell: (item: T, columnId: string, canEdit: boolean, onCellChange: (field: string, value: any) => void) => React.ReactNode;
  renderExpandedRow?: (item: T, canEdit: boolean, onItemChange: (field: string, value: any) => void) => React.ReactNode;
  getRowId: (item: T) => string;
  totalLabel?: string;
  totalValue?: string | number;
  emptyMessage?: string;
  ItemTypes: { [key: string]: string };
  dragDropType: string;
}

const DEFAULT_ITEM_TYPES = { DATA_ITEM: 'dataItem' };

interface DraggableRowProps<T> {
  item: T;
  index: number;
  canEdit: boolean;
  columns: ColumnConfig[];
  onDataChange: (index: number, field: string, value: any) => void;
  onRemove: (id: string) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  renderCell: (item: T, columnId: string, canEdit: boolean, onCellChange: (field: string, value: any) => void) => React.ReactNode;
  renderExpandedRow?: (item: T, canEdit: boolean, onItemChange: (field: string, value: any) => void) => React.ReactNode;
  getRowId: (item: T) => string;
  expandedRows: Set<string>;
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  ItemTypes: { [key: string]: string };
  dragDropType: string;
}

function DraggableRow<T>({
  item,
  index,
  canEdit,
  columns,
  onDataChange,
  onRemove,
  onMove,
  renderCell,
  renderExpandedRow,
  getRowId,
  expandedRows,
  setExpandedRows,
  ItemTypes,
  dragDropType,
}: DraggableRowProps<T>) {
  const [{ isDragging }, drag, preview] = useDrag({
    type: dragDropType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: dragDropType,
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        onMove(item.index, index);
        item.index = index;
      }
    },
  });

  const dragDropRef = (el: HTMLDivElement | null) => {
    drag(el);
    drop(el);
  };

  const rowId = getRowId(item);
  const isExpanded = expandedRows.has(rowId);

  const handleCellChange = (field: string, value: any) => {
    onDataChange(index, field, value);
  };

  return (
    <>
      <div
        ref={dragDropRef}
        className={`grid grid-cols-12 gap-2 items-center py-2 border-b border-border/40 hover:bg-muted/20 px-2 ${
          isDragging ? 'opacity-50' : 'opacity-100'
        }`}
      >
        {canEdit && (
          <div className="col-span-1 flex items-center justify-center cursor-move">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
        )}

        {columns.map((col) => {
          if (!col.visible) return null;
          if (col.id === 'actions') return null;

          return (
            <div key={col.id} className={col.width || 'col-span-1'}>
              {renderCell(item, col.id, canEdit, handleCellChange)}
            </div>
          );
        })}

        <div className="col-span-1 flex items-center justify-center gap-1">
          {renderExpandedRow && (
            <button
              type="button"
              onClick={() => {
                const newExpanded = new Set(expandedRows);
                if (newExpanded.has(rowId)) {
                  newExpanded.delete(rowId);
                } else {
                  newExpanded.add(rowId);
                }
                setExpandedRows(newExpanded);
              }}
              className="p-1 hover:bg-muted rounded"
              title="Expand details"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => onRemove(rowId)}
              className="p-1 text-destructive hover:bg-destructive/10 rounded"
              title="Remove item"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {isExpanded && renderExpandedRow && (
        <div className="grid grid-cols-12 gap-2 py-3 px-4 bg-muted/10 border-b border-border/40">
          <div className="col-span-12">
            {renderExpandedRow(item, canEdit, handleCellChange)}
          </div>
        </div>
      )}
    </>
  );
}

export default function BaseSpreadsheet<T extends Record<string, any>>({
  data,
  columns,
  canEdit,
  onDataChange,
  onRemove,
  onMove,
  onAdd,
  onSave,
  renderCell,
  renderExpandedRow,
  getRowId,
  totalLabel,
  totalValue,
  emptyMessage = 'No items yet',
  ItemTypes = DEFAULT_ITEM_TYPES,
  dragDropType = 'DATA_ITEM',
}: BaseSpreadsheetProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-3">
        {/* Header Row */}
        <div className="grid grid-cols-12 gap-2 items-center py-2 px-2 border-b-2 border-border font-semibold text-xs text-muted-foreground">
          {canEdit && <div className="col-span-1" />}
          {columns.map((col) => (
            col.visible &&
            col.id !== 'actions' && (
              <div key={col.id} className={col.width || 'col-span-1'}>
                {col.label}
              </div>
            )
          ))}
          <div className="col-span-1 text-center">Actions</div>
        </div>

        {/* Data Rows */}
        {data.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
            {canEdit && (
              <button
                onClick={onAdd}
                className="mt-3 inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            )}
          </div>
        ) : (
          <>
            {data.map((item, index) => (
              <DraggableRow
                key={getRowId(item)}
                item={item}
                index={index}
                canEdit={canEdit}
                columns={columns}
                onDataChange={onDataChange}
                onRemove={onRemove}
                onMove={onMove}
                renderCell={renderCell}
                renderExpandedRow={renderExpandedRow}
                getRowId={getRowId}
                expandedRows={expandedRows}
                setExpandedRows={setExpandedRows}
                ItemTypes={ItemTypes}
                dragDropType={dragDropType}
              />
            ))}

            {/* Total Row */}
            {(totalLabel || totalValue) && (
              <div className="grid grid-cols-12 gap-2 items-center py-3 px-2 border-t-2 border-border font-semibold">
                <div className={`col-span-${canEdit ? '11' : '12'} text-right text-foreground`}>
                  {totalLabel || 'Total'}
                </div>
                <div className="col-span-1 text-right text-lg text-primary">
                  {typeof totalValue === 'number'
                    ? `$${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    : totalValue}
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        {canEdit && data.length > 0 && (
          <div className="flex gap-2 pt-4 border-t border-border">
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
            <button
              onClick={onSave}
              className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
    </DndProvider>
  );
}
